import { useEffect, useId, useRef, type ReactNode } from 'react';
import { NavIcon } from './NavIcon.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional one-line explainer rendered under the title. */
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Render above other open modals (used by stacked confirmations). */
  elevated?: boolean;
};

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

// Tokens of every currently-open Modal, in open order. Only the topmost one
// reacts to Escape/Tab, so a confirmation stacked over another modal dismisses
// just itself rather than both.
const modalStack: string[] = [];

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  elevated = false,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  // Keep the latest onClose without making it an effect dependency: callers
  // pass inline arrows, and re-running the effect on every render would yank
  // focus back to the first field on each keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    // Remember what had focus so we can restore it when the dialog closes.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    modalStack.push(titleId);

    function focusable(): HTMLElement[] {
      const root = panelRef.current;
      if (!root) return [];
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    // Move focus into the dialog. Skip the header ✕ — it is first in DOM order,
    // and landing on it would rob form dialogs of focus on their first field.
    // It stays in the Tab cycle; it just isn't the opening target.
    const initial = focusable();
    (initial.find((el) => el !== closeRef.current) ?? initial[0] ?? panelRef.current)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      // Only the topmost open modal handles keys.
      if (modalStack[modalStack.length - 1] !== titleId) return;
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap Tab focus within the dialog so it can't wander to the page behind.
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = modalStack.indexOf(titleId);
      if (i !== -1) modalStack.splice(i, 1);
      previouslyFocused?.focus?.();
    };
  }, [open, titleId]);

  if (!open) return null;

  // The backdrop deliberately has no click handler: these dialogs hold form
  // input, and a stray click (or releasing a text selection outside the panel)
  // would discard it. Escape and the explicit Cancel/Close controls are the
  // only ways out.
  //
  // `!m-0`: this renders in place, not through a portal, so a call site inside
  // a `space-y-*` parent hands the backdrop that parent's margin-top — and a
  // margin on a `fixed inset-0` box shifts it, leaving a strip of the page
  // above it uncovered and clickable. IMPORTANT, not plain `m-0`: Tailwind 3's
  // `.space-y-4 > :not([hidden]) ~ :not([hidden])` outranks a single class.
  return (
    <div
      className={`fixed inset-0 !m-0 ${elevated ? 'z-[60]' : 'z-50'} flex items-center justify-center bg-slate-900/40 p-4`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`w-full ${SIZES[size]} rounded-[var(--ui-card-radius,0.75rem)] bg-[var(--ui-card-bg,#ffffff)] shadow-xl focus:outline-none`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--ui-card-divider,#f1f5f9)] px-6 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold [overflow-wrap:anywhere] text-[var(--ui-text-ink,#0f172a)]">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-0.5 text-sm text-[var(--ui-text-hint,#64748b)] [overflow-wrap:anywhere]">{subtitle}</p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="-mr-2 -mt-1 shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <NavIcon name="x" size={18} />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer !== undefined && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--ui-card-divider,#f1f5f9)] px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
